import axios from "axios";
import Cookies from "js-cookie";

/**
 * Attaches the logged-in user's token to every API request, and ends the session
 * when — and only when — that token is the thing the server rejected.
 *
 * The app stores a token at login but historically never sent it, which is why
 * the backend had to permit every request. Registering this once covers all
 * call sites without touching any of them individually — new code gets the
 * header for free, so no future request can accidentally go out unauthenticated.
 *
 * Imported for its side effects from index.js, before anything renders.
 */

const TOKEN_COOKIE = "TOKEN";

// Only the login/registration endpoints are reachable without a token, and
// sending a stale one to them can only cause confusion.
const PUBLIC_PATHS = ["/auth/login", "/auth/register", "/auth/forgotPassword", "/auth/verifyUser"];

const isPublic = (url = "") => PUBLIC_PATHS.some((p) => url.includes(p));

axios.interceptors.request.use((config) => {
  const token = Cookies.get(TOKEN_COOKIE);
  if (token && !isPublic(config.url) && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Pulls the message out of an error body without assuming one error shape. The
 * API answers with two: ErrorResponse ({error, errorDescription}) from the JWT
 * filter, and Spring's ProblemDetail ({detail, description}) from the exception
 * handler.
 */
const errorText = (error) => {
  const body = error?.response?.data;
  if (!body) return "";
  if (typeof body === "string") return body.toLowerCase();
  return [body.errorDescription, body.description, body.detail, body.message, body.error]
    .filter((part) => typeof part === "string")
    .join(" ")
    .toLowerCase();
};

/**
 * The server saying this *token* is no good, as opposed to refusing this one
 * request.
 *
 * Status alone cannot tell these apart: an expired token, a forged signature and
 * an AccessGuard denial for somebody else's account all come back as 403. Only
 * the body distinguishes them, so it is what decides whether to sign out.
 */
const TOKEN_REJECTED_MESSAGES = [
  "missing jwt token",
  "invalid jwt token",
  "jwt signature is invalid",
  "jwt token has expired",
];

const serverRejectedToken = (error) =>
  TOKEN_REJECTED_MESSAGES.some((message) => errorText(error).includes(message));

/**
 * Reads exp out of the stored token. Decoded, never verified — the server is the
 * only thing that validates a token; this only decides whether to show the user
 * a sign-in page, so a forged exp costs nothing.
 */
const tokenHasExpired = () => {
  const token = Cookies.get(TOKEN_COOKIE);
  if (!token) return true;

  try {
    const [, payload] = token.split(".");
    if (!payload) return false;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const { exp } = JSON.parse(atob(padded));

    // A token with no exp never expires on its own; leave it to the server.
    return typeof exp === "number" ? Date.now() >= exp * 1000 : false;
  } catch {
    // Unreadable token: do not end a session that may be working fine.
    return false;
  }
};

const endSession = () => {
  Cookies.remove(TOKEN_COOKIE);
  Cookies.remove("U_ID");
  Cookies.remove("U_NAME");
  Cookies.remove("U_TYPE");
  if (!window.location.pathname.startsWith("/login")) {
    window.location.assign("/login");
  }
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const hasSession = Boolean(Cookies.get(TOKEN_COOKIE));

    // This used to sign the user out on any 401 or 403 from any request, so a
    // single endpoint that was missing, misrouted or simply not permitted for
    // this account ended the whole session. One absent endpoint on the profile
    // screen was enough to log people out the moment they opened it.
    //
    // Now the session ends only when the token itself is rejected, or has
    // already expired. Anything else is left to the caller, which can show an
    // error in the one panel that failed instead of ejecting the user.
    if (hasSession && (status === 401 || status === 403)) {
      if (serverRejectedToken(error) || tokenHasExpired()) {
        endSession();
      }
    }

    return Promise.reject(error);
  }
);
